import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CLASS_INTEREST_OPTIONS } from '../lib/types';
import { errorMessage } from '../lib/format';
import './splash.css';

// Formats as (###)-###-#### while typing, capped at 10 digits.
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  const area = digits.slice(0, 3);
  const mid = digits.slice(3, 6);
  const last = digits.slice(6, 10);
  if (digits.length > 6) return `(${area})-${mid}-${last}`;
  if (digits.length > 3) return `(${area})-${mid}`;
  if (digits.length > 0) return `(${area}`;
  return '';
}

export function InquirePage() {
  const [params] = useSearchParams();
  const classParam = params.get('class');
  const initialInterest = (CLASS_INTEREST_OPTIONS as readonly string[]).includes(classParam ?? '')
    ? (classParam as string)
    : '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [dogName, setDogName] = useState('');
  const [dogBreed, setDogBreed] = useState('');
  const [dogAge, setDogAge] = useState('');
  const [classInterest, setClassInterest] = useState(initialInterest);
  const [comments, setComments] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.from('inquiries').insert({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      dog_name: dogName.trim() || null,
      dog_breed: dogBreed.trim() || null,
      dog_age: dogAge.trim() || null,
      class_interest: classInterest || null,
      comments: comments.trim() || null,
    });
    setBusy(false);
    if (error) {
      setError(errorMessage(error));
      return;
    }
    setSent(true);
  }

  return (
    <div className="splash">
      <div className="splash-bg" aria-hidden />
      <nav className="s-nav scrolled">
        <Link to="/" className="s-brand">
          <img src="/img/logo.png" alt="" />
          <span>
            Top Dogs
            <small>Training Academy</small>
          </span>
        </Link>
      </nav>

      <main className="splash-main">
        <section className="section-pad inquiry-section">
          <div className="wrap-x">
            <div className="inquiry-card">
              {sent ? (
                <div className="inquiry-thanks">
                  <span className="eyebrow">Thank you</span>
                  <h1 className="section-title">Thanks for your inquiry!</h1>
                  <p className="section-lead">Our team will reach out to you shortly.</p>
                  <Link className="s-btn s-btn-primary" to="/">
                    Back to home
                  </Link>
                </div>
              ) : (
                <>
                  <span className="eyebrow">Get started</span>
                  <h1 className="section-title">Tell us about you and your dog</h1>
                  <p className="section-lead" style={{ marginTop: '0.6rem' }}>
                    Fill this out and we’ll be in touch to get you booked into a class.
                  </p>
                  <form className="inquiry-form" onSubmit={submit}>
                    <div className="inquiry-row">
                      <div className="inquiry-field">
                        <label htmlFor="firstName">First name</label>
                        <input
                          id="firstName"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="inquiry-field">
                        <label htmlFor="lastName">Last name</label>
                        <input
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="inquiry-row">
                      <div className="inquiry-field">
                        <label htmlFor="phone">Phone number</label>
                        <input
                          id="phone"
                          type="tel"
                          placeholder="(123)-456-7890"
                          value={phone}
                          onChange={(e) => setPhone(formatPhone(e.target.value))}
                          required
                        />
                      </div>
                      <div className="inquiry-field">
                        <label htmlFor="email">Email</label>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="inquiry-row">
                      <div className="inquiry-field">
                        <label htmlFor="dogName">Dog’s name</label>
                        <input
                          id="dogName"
                          value={dogName}
                          onChange={(e) => setDogName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="inquiry-field">
                        <label htmlFor="dogBreed">Dog’s breed</label>
                        <input
                          id="dogBreed"
                          value={dogBreed}
                          onChange={(e) => setDogBreed(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="inquiry-row">
                      <div className="inquiry-field">
                        <label htmlFor="dogAge">Dog’s age</label>
                        <input
                          id="dogAge"
                          placeholder="e.g. 2 years or 12 weeks"
                          value={dogAge}
                          onChange={(e) => setDogAge(e.target.value)}
                          required
                        />
                      </div>
                      <div className="inquiry-field">
                        <label htmlFor="classInterest">Type of class</label>
                        <select
                          id="classInterest"
                          value={classInterest}
                          onChange={(e) => setClassInterest(e.target.value)}
                          required
                        >
                          <option value="" disabled>
                            Choose one
                          </option>
                          {CLASS_INTEREST_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="inquiry-field">
                      <label htmlFor="comments">Comments</label>
                      <textarea
                        id="comments"
                        rows={4}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                      />
                    </div>
                    {error && <p className="inquiry-error">{error}</p>}
                    <button type="submit" className="s-btn s-btn-gold" disabled={busy}>
                      {busy ? 'Sending…' : 'Send inquiry'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
